/**
 * 광고 «바로 신청» 경로 — /advertise·제안 페이지·현장 상세·빈 자리 안내에서 /ads/buy 로 상품·대상을 미리 골라 보낸다.
 *   /ads/buy?product=site&site=<uuid>   현장 광고(그 현장)
 *   /ads/buy?product=district&district=부산:연제구   시군구 배너(그 시군구)
 *   /ads/buy?product=home               홈 배너
 *   from=  출발 지면(계측용 · 화면 동작에는 쓰지 않음)
 * 순수 함수(IO 없음) — 서버·클라이언트·테스트 공용. ⛔ 금액 없음.
 */
export type BuyProduct = 'site' | 'district' | 'home';

export interface BuyPreselect {
  product: BuyProduct | null;
  site: string | null;
  district: string | null;
  from: string | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DISTRICT_RE = /^[^:\s]{1,20}:[^:]{1,20}$/;
const FROM_RE = /^[a-z0-9_]{1,30}$/;

export const isBuyProduct = (v: unknown): v is BuyProduct => v === 'site' || v === 'district' || v === 'home';
export const isSiteId = (v: unknown): v is string => typeof v === 'string' && UUID_RE.test(v);
export const isDistrictKey = (v: unknown): v is string => typeof v === 'string' && DISTRICT_RE.test(v);

/** /ads/buy 바로 신청 주소. 상품과 맞지 않는 대상 값은 버린다. */
export function buyHref(o: { product: BuyProduct; site?: string | null; district?: string | null; from?: string | null }): string {
  const q = new URLSearchParams();
  q.set('product', o.product);
  if (o.product === 'site' && isSiteId(o.site)) q.set('site', o.site.toLowerCase());
  if (o.product === 'district' && isDistrictKey(o.district)) q.set('district', o.district);
  if (o.from && FROM_RE.test(o.from)) q.set('from', o.from);
  return `/ads/buy?${q.toString()}`;
}

type SpLike = { get(k: string): string | null } | Record<string, string | string[] | undefined>;
function pick(sp: SpLike, k: string): string | null {
  if (typeof (sp as any).get === 'function') return (sp as { get(k: string): string | null }).get(k);
  const v = (sp as Record<string, string | string[] | undefined>)[k];
  return typeof v === 'string' ? v : Array.isArray(v) ? v[0] ?? null : null;
}

/** /ads/buy 주소에서 미리 고를 값 읽기. 잘못된 값은 null. 대상만 있고 상품이 없으면 대상으로 상품을 정한다. */
export function parseBuyPreselect(sp: SpLike): BuyPreselect {
  const rawP = pick(sp, 'product');
  const site = pick(sp, 'site');
  const district = pick(sp, 'district');
  const from = pick(sp, 'from');
  let product: BuyProduct | null = isBuyProduct(rawP) ? rawP : null;
  if (!product && isSiteId(site)) product = 'site';
  if (!product && isDistrictKey(district)) product = 'district';
  return {
    product,
    site: product === 'site' && isSiteId(site) ? site.toLowerCase() : null,
    district: product === 'district' && isDistrictKey(district) ? district : null,
    from: from && FROM_RE.test(from) ? from : null,
  };
}

/** 광고 안내(/advertise)의 상품 단락 주소 — 출처·시군구·현장을 주소로 넘기면 그 단락의 «바로 신청»이 이어받는다. */
export function advertiseHref(o: { product: BuyProduct; from?: string | null; district?: string | null; site?: string | null }): string {
  const q = new URLSearchParams();
  if (o.from && FROM_RE.test(o.from)) q.set('from', o.from);
  if (isDistrictKey(o.district)) q.set('sg', o.district);
  if (isSiteId(o.site)) q.set('site', o.site.toLowerCase());
  const s = q.toString();
  return `/advertise${s ? `?${s}` : ''}`; // ads-top — 해시 앵커 없이 맨 위부터(Node 요청)
}
